import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import StockSignalsTable from './StockSignalsTable';
import BASE_URL from './config';
import BoughtSharesTable from './BoughtSharesTable';
import INTERVAL from './interval';

function App() {
  const orderIntervalRef = useRef(null);
  const [log, setLog] = useState([]);
  const intervalRef = useRef(null);
  const [refreshSignals, setRefreshSignals] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const marketCheckIntervalRef = useRef(null);

  const logMsg = (msg) => {
    setLog((prev) => [...prev.slice(-20), `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  const startMarketCheck = useCallback(() => {
    if (marketCheckIntervalRef.current) return;

    logMsg('📈 Starting market check every 1 second...');

    let isRequestInProgress = false;

    marketCheckIntervalRef.current = setInterval(() => {
      if (isRequestInProgress) return;

      isRequestInProgress = true;

      axios
        .post(`${BASE_URL}/check-should-buy`)
        .then((res) => {
          logMsg(`🧠 Market check result: ${res.data.message || 'done'}`);
        })
        .catch((err) => {
          logMsg(`❌ Market check failed: ${err.message}`);
        })
        .finally(() => {
          isRequestInProgress = false;
        });
    }, INTERVAL.MARKET_CHECK * 1000);
  }, []);

  const placeOrdersOnly = async () => {
    try {
      logMsg('📝 Placing orders only...');
      await axios.post(`${BASE_URL}/place-orders`);
      logMsg('✅ Orders placed.');
    } catch (err) {
      logMsg(`❌ Failed to place orders: ${err.message}`);
    }
  };

  const startPlacingOrders = () => {
    if (orderIntervalRef.current) return;
    placeOrdersOnly();
    orderIntervalRef.current = setInterval(() => {
      placeOrdersOnly();
    }, INTERVAL.PLACE_ORDERS);
  };

  const stopPlacingOrders = () => {
    if (orderIntervalRef.current) {
      clearInterval(orderIntervalRef.current);
      orderIntervalRef.current = null;
      logMsg('🛑 Stopped placing orders.');
    }
  };

  const handleIntradayCall = async (label = 'start') => {
    try {
      const res = await axios.post(`${BASE_URL}/start-intraday`);
      logMsg(`✅ ${label === 'start' ? 'Pre-check' : 'Check'} success: ${res.data.message || 'done'}`);
      setRefreshSignals((prev) => prev + 1);
    } catch (err) {
      logMsg(`❌ ${label === 'start' ? 'Failed to start' : 'Periodic check failed'}: ${err.message}`);
    }
  };

  const startTrading = async () => {
    if (intervalRef.current) return;

    logMsg('🚀 Starting with /start-intraday...');
    await handleIntradayCall('start');

    setCountdown(INTERVAL.START_TRADING);

    intervalRef.current = setInterval(async () => {
      await handleIntradayCall('interval');
      setCountdown(INTERVAL.START_TRADING);
    }, INTERVAL.START_TRADING * 1000); // convert to milliseconds
  };

  // countdown updater
  useEffect(() => {
    if (countdown === null) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdown]);

  const stopTrading = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      setCountdown(null);
      logMsg('🛑 Trading stopped.');
    }
  };

  useEffect(() => {
    startMarketCheck();
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      if (orderIntervalRef.current) {
        clearInterval(orderIntervalRef.current);
        orderIntervalRef.current = null;
      }

      if (marketCheckIntervalRef.current) {
        clearInterval(marketCheckIntervalRef.current);
        marketCheckIntervalRef.current = null;
      }

      console.log('🧹 Cleared all intervals on unmount');
    };
  }, [startMarketCheck]);

  const stopMarketCheck = () => {
    if (marketCheckIntervalRef.current) {
      clearInterval(marketCheckIntervalRef.current);
      marketCheckIntervalRef.current = null;
      logMsg('🛑 Stopped market check.');
    }
  };

  const resetTradingData = async () => {
    try {
      logMsg('⚠️ Resetting stock signals and orders...');
      const res = await axios.post(`${BASE_URL}/reset-trading-data`);
      logMsg(`✅ ${res.data.message}`);
      setRefreshSignals((prev) => prev + 1);
    } catch (err) {
      logMsg(`❌ Reset failed: ${err.message}`);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-3 text-primary">📈 Algo Trading Control Panel</h2>

      <div className="mb-3">
        {countdown !== null && (
          <p>⏳ Next call in: {Math.floor(countdown / 60)}m {countdown % 60}s</p>
        )}
      </div>

      <div className="row mb-4">
        {/* Column 1: Start/Stop Trading */}
        <div className="col-md-4 d-flex flex-column gap-2">
          <button onClick={startTrading} className="btn btn-success">
            <i className="fas fa-play me-2"></i> Start Trading
          </button>
          <button onClick={stopTrading} className="btn btn-outline-danger">
            <i className="fas fa-stop me-2"></i> Stop Trading
          </button>
        </div>

        {/* Column 2: Place/Stop Orders */}
        <div className="col-md-4 d-flex flex-column gap-2">
          <button onClick={startPlacingOrders} className="btn btn-primary">
            <i className="fas fa-pen me-2"></i> Start Order Placement
          </button>
          <button onClick={stopPlacingOrders} className="btn btn-outline-primary">
            <i className="fas fa-stop me-2"></i> Stop Order Placement
          </button>
        </div>

        {/* Column 3: Market Check */}
        <div className="col-md-4 d-flex flex-column gap-2">
          <button onClick={startMarketCheck} className="btn btn-warning text-dark">
            <i className="fas fa-chart-line me-2"></i> Start Market Monitor
          </button>
          <button onClick={stopMarketCheck} className="btn btn-outline-warning text-dark">
            <i className="fas fa-stop me-2"></i> Stop Market Monitor
          </button>
        </div>
      </div>


      <StockSignalsTable refreshTrigger={refreshSignals} />

      <BoughtSharesTable />

      <div className="card">
        <div className="card-header bg-dark text-white">📋 Logs (Last 20)</div>
        <div className="card-body" style={{ maxHeight: 300, overflowY: 'auto', fontFamily: 'monospace' }}>
          {log.length === 0 && <div className="text-muted">No activity yet...</div>}
          <ul className="list-unstyled mb-0">
            {log.map((msg, idx) => (
              <li key={idx}>{msg}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="card-footer text-center mt-5">
        <button onClick={resetTradingData} className="btn btn-outline-danger">
          <i className="fas fa-broom me-2"></i> Reset Trading Data
        </button>
      </div>

    </div>
  );
}

export default App;
